import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';

const TABS = [
  { key: 'states', label: 'States' },
  { key: 'districts', label: 'Districts' },
  { key: 'cities', label: 'Cities' },
  { key: 'areas', label: 'Areas' },
  { key: 'categories', label: 'Categories' },
];

function triggerDownload(blobOrText, filename, mime = 'text/csv') {
  const blob = blobOrText instanceof Blob ? blobOrText : new Blob([blobOrText], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function buildCsv(headers, rows) {
  const escape = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(','), ...rows.map((r) => r.map(escape).join(','))];
  return lines.join('\n');
}

function ConfirmDeleteButton({ onConfirm, label = 'Delete' }) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <span className="inline-flex items-center gap-2">
        <button
          onClick={() => { onConfirm(); setConfirming(false); }}
          className="text-xs font-semibold text-red-600 hover:underline"
        >
          Confirm?
        </button>
        <button
          onClick={() => setConfirming(false)}
          className="text-xs font-semibold text-gray-400 hover:underline"
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="text-xs font-semibold text-red-600 hover:underline"
    >
      {label}
    </button>
  );
}

function BulkToolbar({
  selectedCount,
  onBulkDelete,
  onImportFile,
  onExport,
  onDownloadTemplate,
  showTrash,
  onToggleTrash,
  importing,
  exporting,
}) {
  const fileInputRef = useRef(null);

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={importing}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-50"
      >
        {importing ? 'Importing…' : '↑ Import CSV'}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onImportFile(file);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={onExport}
        disabled={exporting}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-50"
      >
        {exporting ? 'Exporting…' : '↓ Export CSV'}
      </button>
      <button
        type="button"
        onClick={onDownloadTemplate}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100"
      >
        Sample Format
      </button>
      <button
        type="button"
        onClick={onToggleTrash}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-100"
      >
        {showTrash ? '← Back to List' : 'View Trash'}
      </button>
      {selectedCount > 0 && (
        <button
          type="button"
          onClick={onBulkDelete}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 ml-auto"
        >
          Delete Selected ({selectedCount})
        </button>
      )}
    </div>
  );
}

function TrashPanel({ items, loading, extraLabel, extraValue, onRestore }) {
  const [selected, setSelected] = useState(new Set());

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div>
      {selected.size > 0 && (
        <div className="px-4 pt-3">
          <button
            type="button"
            onClick={() => { onRestore(Array.from(selected)); setSelected(new Set()); }}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
          >
            Restore Selected ({selected.size})
          </button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="px-4 py-2 w-8"></th>
            <th className="text-left px-4 py-2 font-semibold">Name</th>
            {extraLabel && <th className="text-left px-4 py-2 font-semibold">{extraLabel}</th>}
            <th className="text-left px-4 py-2 font-semibold">Deleted</th>
            <th className="text-left px-4 py-2 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Trash is empty.</td></tr>
          ) : (
            items.map((item) => (
              <tr key={item.id} className="border-t">
                <td className="px-4 py-2">
                  <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)} />
                </td>
                <td className="px-4 py-2 font-medium">{item.name}</td>
                {extraLabel && <td className="px-4 py-2 text-gray-500">{extraValue(item) || '—'}</td>}
                <td className="px-4 py-2 text-gray-400 text-xs">
                  {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-2">
                  <button onClick={() => onRestore([item.id])} className="text-xs font-semibold text-[#10B981] hover:underline">
                    Restore
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function StatesTab() {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', status: 'active' });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trashedItems, setTrashedItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/states');
      setStates(data);
    } catch (err) {
      setError('Failed to load states.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/admin/states', form);
      setStates((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ name: '' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to add state.'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, status: s.status });
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.put(`/admin/states/${id}`, editForm);
      setStates((prev) => prev.map((s) => (s.id === id ? data : s)));
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update state.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/states/${id}`);
      setStates((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete state.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === states.length ? new Set() : new Set(states.map((s) => s.id))));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected state(s)?`)) return;
    try {
      const { data } = await api.post('/admin/states/bulk-delete', { ids: Array.from(selectedIds) });
      setStates((prev) => prev.filter((s) => !data.deleted.includes(s.id)));
      setSelectedIds(new Set());
      if (data.skipped.length > 0) {
        alert(`Deleted ${data.deleted.length}. Skipped ${data.skipped.length}:\n` + data.skipped.map((s) => `${s.name}: ${s.reason}`).join('\n'));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const loadTrashed = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get('/admin/states/trashed');
      setTrashedItems(data);
    } catch (err) {
      alert('Failed to load trash.');
    } finally {
      setTrashLoading(false);
    }
  };

  const toggleTrashView = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) loadTrashed();
  };

  const handleRestore = async (ids) => {
    try {
      await api.post('/admin/states/restore', { ids });
      setTrashedItems((prev) => prev.filter((i) => !ids.includes(i.id)));
      load();
    } catch (err) {
      alert('Restore failed.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/states/export', { responseType: 'blob' });
      triggerDownload(res.data, 'states.csv');
    } catch (err) {
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = buildCsv(['name', 'status'], [['Karnataka', 'active']]);
    triggerDownload(csv, 'states_template.csv');
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/states/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Import done. Created: ${data.created}, Updated: ${data.updated}, Errors: ${data.errors.length}` +
        (data.errors.length ? '\n' + data.errors.slice(0, 5).map((e) => `Row ${e.row}: ${e.reason}`).join('\n') : ''));
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-bold text-[#0D1B3D] mb-3">Add State</h3>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="State name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm sm:col-span-2"
          />
          <button
            type="submit"
            disabled={saving}
            className="bg-[#2563EB] text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add State'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <BulkToolbar
          selectedCount={selectedIds.size}
          onBulkDelete={handleBulkDelete}
          onImportFile={handleImportFile}
          onExport={handleExport}
          onDownloadTemplate={handleDownloadTemplate}
          showTrash={showTrash}
          onToggleTrash={toggleTrashView}
          importing={importing}
          exporting={exporting}
        />

        {showTrash ? (
          <TrashPanel
            items={trashedItems}
            loading={trashLoading}
            extraLabel={null}
            extraValue={() => null}
            onRestore={handleRestore}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#EFF6FF] text-[#0D1B3D]">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={states.length > 0 && selectedIds.size === states.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
              ) : states.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No states yet.</td></tr>
              ) : (
                states.map((s) => (
                  <tr key={s.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} />
                    </td>
                    {editingId === s.id ? (
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="border rounded px-2 py-1 text-xs w-full"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 flex gap-3">
                          <button onClick={() => saveEdit(s.id)} className="text-xs font-semibold text-[#10B981] hover:underline">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs font-semibold text-gray-500 hover:underline">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium">{s.name}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 flex gap-3">
                          <button onClick={() => startEdit(s)} className="text-xs font-semibold text-[#2563EB] hover:underline">Edit</button>
                          <ConfirmDeleteButton onConfirm={() => handleDelete(s.id)} />
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function DistrictsTab() {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ state_id: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', status: 'active' });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trashedItems, setTrashedItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statesRes, districtsRes] = await Promise.all([
        api.get('/admin/states'),
        api.get('/admin/districts'),
      ]);
      setStates(statesRes.data);
      setDistricts(districtsRes.data);
    } catch (err) {
      setError('Failed to load districts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/admin/districts', form);
      setDistricts((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ state_id: form.state_id, name: '' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to add district.'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (d) => {
    setEditingId(d.id);
    setEditForm({ name: d.name, status: d.status });
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.put(`/admin/districts/${id}`, editForm);
      setDistricts((prev) => prev.map((d) => (d.id === id ? data : d)));
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update district.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/districts/${id}`);
      setDistricts((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete district.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === districts.length ? new Set() : new Set(districts.map((d) => d.id))));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected district(s)?`)) return;
    try {
      const { data } = await api.post('/admin/districts/bulk-delete', { ids: Array.from(selectedIds) });
      setDistricts((prev) => prev.filter((d) => !data.deleted.includes(d.id)));
      setSelectedIds(new Set());
      if (data.skipped.length > 0) {
        alert(`Deleted ${data.deleted.length}. Skipped ${data.skipped.length}:\n` + data.skipped.map((s) => `${s.name}: ${s.reason}`).join('\n'));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const loadTrashed = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get('/admin/districts/trashed');
      setTrashedItems(data);
    } catch (err) {
      alert('Failed to load trash.');
    } finally {
      setTrashLoading(false);
    }
  };

  const toggleTrashView = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) loadTrashed();
  };

  const handleRestore = async (ids) => {
    try {
      await api.post('/admin/districts/restore', { ids });
      setTrashedItems((prev) => prev.filter((i) => !ids.includes(i.id)));
      load();
    } catch (err) {
      alert('Restore failed.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/districts/export', { responseType: 'blob' });
      triggerDownload(res.data, 'districts.csv');
    } catch (err) {
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = buildCsv(['state_name', 'name', 'status'], [['Karnataka', 'Bengaluru', 'active']]);
    triggerDownload(csv, 'districts_template.csv');
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/districts/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Import done. Created: ${data.created}, Updated: ${data.updated}, Errors: ${data.errors.length}` +
        (data.errors.length ? '\n' + data.errors.slice(0, 5).map((e) => `Row ${e.row}: ${e.reason}`).join('\n') : ''));
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-bold text-[#0D1B3D] mb-3">Add District</h3>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={form.state_id}
            onChange={(e) => setForm({ ...form, state_id: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Select state *</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="District name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={saving}
            className="bg-[#2563EB] text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add District'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <BulkToolbar
          selectedCount={selectedIds.size}
          onBulkDelete={handleBulkDelete}
          onImportFile={handleImportFile}
          onExport={handleExport}
          onDownloadTemplate={handleDownloadTemplate}
          showTrash={showTrash}
          onToggleTrash={toggleTrashView}
          importing={importing}
          exporting={exporting}
        />

        {showTrash ? (
          <TrashPanel
            items={trashedItems}
            loading={trashLoading}
            extraLabel="State"
            extraValue={(item) => item.state?.name}
            onRestore={handleRestore}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#EFF6FF] text-[#0D1B3D]">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={districts.length > 0 && selectedIds.size === districts.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">State</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
              ) : districts.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No districts yet.</td></tr>
              ) : (
                districts.map((d) => (
                  <tr key={d.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds.has(d.id)} onChange={() => toggleSelect(d.id)} />
                    </td>
                    {editingId === d.id ? (
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="border rounded px-2 py-1 text-xs w-full"
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-500">{d.state?.name || '—'}</td>
                        <td className="px-4 py-2">
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 flex gap-3">
                          <button onClick={() => saveEdit(d.id)} className="text-xs font-semibold text-[#10B981] hover:underline">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs font-semibold text-gray-500 hover:underline">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium">{d.name}</td>
                        <td className="px-4 py-3 text-gray-500">{d.state?.name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {d.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 flex gap-3">
                          <button onClick={() => startEdit(d)} className="text-xs font-semibold text-[#2563EB] hover:underline">Edit</button>
                          <ConfirmDeleteButton onConfirm={() => handleDelete(d.id)} />
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CitiesTab() {
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ district_id: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', status: 'active' });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trashedItems, setTrashedItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [districtsRes, citiesRes] = await Promise.all([
        api.get('/admin/districts'),
        api.get('/admin/cities'),
      ]);
      setDistricts(districtsRes.data);
      setCities(citiesRes.data);
    } catch (err) {
      setError('Failed to load cities.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/admin/cities', form);
      setCities((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ district_id: form.district_id, name: '' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to add city.'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ name: c.name, status: c.status });
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.put(`/admin/cities/${id}`, editForm);
      setCities((prev) => prev.map((c) => (c.id === id ? data : c)));
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update city.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/cities/${id}`);
      setCities((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete city.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === cities.length ? new Set() : new Set(cities.map((c) => c.id))));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected city/cities?`)) return;
    try {
      const { data } = await api.post('/admin/cities/bulk-delete', { ids: Array.from(selectedIds) });
      setCities((prev) => prev.filter((c) => !data.deleted.includes(c.id)));
      setSelectedIds(new Set());
      if (data.skipped.length > 0) {
        alert(`Deleted ${data.deleted.length}. Skipped ${data.skipped.length}:\n` + data.skipped.map((s) => `${s.name}: ${s.reason}`).join('\n'));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const loadTrashed = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get('/admin/cities/trashed');
      setTrashedItems(data);
    } catch (err) {
      alert('Failed to load trash.');
    } finally {
      setTrashLoading(false);
    }
  };

  const toggleTrashView = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) loadTrashed();
  };

  const handleRestore = async (ids) => {
    try {
      await api.post('/admin/cities/restore', { ids });
      setTrashedItems((prev) => prev.filter((i) => !ids.includes(i.id)));
      load();
    } catch (err) {
      alert('Restore failed.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/cities/export', { responseType: 'blob' });
      triggerDownload(res.data, 'cities.csv');
    } catch (err) {
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = buildCsv(
      ['district_name', 'name', 'slug', 'latitude', 'longitude', 'status'],
      [['Bengaluru', 'Bengaluru', 'bengaluru', '12.9716', '77.5946', 'active']]
    );
    triggerDownload(csv, 'cities_template.csv');
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/cities/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Import done. Created: ${data.created}, Updated: ${data.updated}, Errors: ${data.errors.length}` +
        (data.errors.length ? '\n' + data.errors.slice(0, 5).map((e) => `Row ${e.row}: ${e.reason}`).join('\n') : ''));
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-bold text-[#0D1B3D] mb-3">Add City</h3>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={form.district_id}
            onChange={(e) => setForm({ ...form, district_id: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Select district *</option>
            {districts.map((d) => (
              <option key={d.id} value={d.id}>{d.name} ({d.state?.name})</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="City name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={saving}
            className="bg-[#2563EB] text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add City'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <BulkToolbar
          selectedCount={selectedIds.size}
          onBulkDelete={handleBulkDelete}
          onImportFile={handleImportFile}
          onExport={handleExport}
          onDownloadTemplate={handleDownloadTemplate}
          showTrash={showTrash}
          onToggleTrash={toggleTrashView}
          importing={importing}
          exporting={exporting}
        />

        {showTrash ? (
          <TrashPanel
            items={trashedItems}
            loading={trashLoading}
            extraLabel="District"
            extraValue={(item) => item.district?.name}
            onRestore={handleRestore}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#EFF6FF] text-[#0D1B3D]">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={cities.length > 0 && selectedIds.size === cities.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">District</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
              ) : cities.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No cities yet.</td></tr>
              ) : (
                cities.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} />
                    </td>
                    {editingId === c.id ? (
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="border rounded px-2 py-1 text-xs w-full"
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-500">{c.district?.name || '—'}</td>
                        <td className="px-4 py-2">
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 flex gap-3">
                          <button onClick={() => saveEdit(c.id)} className="text-xs font-semibold text-[#10B981] hover:underline">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs font-semibold text-gray-500 hover:underline">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium">{c.name}</td>
                        <td className="px-4 py-3 text-gray-500">{c.district?.name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 flex gap-3">
                          <button onClick={() => startEdit(c)} className="text-xs font-semibold text-[#2563EB] hover:underline">Edit</button>
                          <ConfirmDeleteButton onConfirm={() => handleDelete(c.id)} />
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AreasTab() {
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ city_id: '', name: '', pincode: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', pincode: '', status: 'active' });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trashedItems, setTrashedItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [citiesRes, areasRes] = await Promise.all([
        api.get('/admin/cities'),
        api.get('/admin/areas'),
      ]);
      setCities(citiesRes.data);
      setAreas(areasRes.data);
    } catch (err) {
      setError('Failed to load areas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/admin/areas', form);
      setAreas((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ city_id: form.city_id, name: '', pincode: '' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to add area.'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({ name: a.name, pincode: a.pincode || '', status: a.status });
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.put(`/admin/areas/${id}`, editForm);
      setAreas((prev) => prev.map((a) => (a.id === id ? data : a)));
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update area.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/areas/${id}`);
      setAreas((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete area.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === areas.length ? new Set() : new Set(areas.map((a) => a.id))));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected area(s)?`)) return;
    try {
      const { data } = await api.post('/admin/areas/bulk-delete', { ids: Array.from(selectedIds) });
      setAreas((prev) => prev.filter((a) => !data.deleted.includes(a.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const loadTrashed = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get('/admin/areas/trashed');
      setTrashedItems(data);
    } catch (err) {
      alert('Failed to load trash.');
    } finally {
      setTrashLoading(false);
    }
  };

  const toggleTrashView = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) loadTrashed();
  };

  const handleRestore = async (ids) => {
    try {
      await api.post('/admin/areas/restore', { ids });
      setTrashedItems((prev) => prev.filter((i) => !ids.includes(i.id)));
      load();
    } catch (err) {
      alert('Restore failed.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/areas/export', { responseType: 'blob' });
      triggerDownload(res.data, 'areas.csv');
    } catch (err) {
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = buildCsv(
      ['city_name', 'name', 'pincode', 'latitude', 'longitude', 'status'],
      [['Bengaluru', 'MG Road', '560001', '12.9757', '77.6062', 'active']]
    );
    triggerDownload(csv, 'areas_template.csv');
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/areas/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Import done. Created: ${data.created}, Updated: ${data.updated}, Errors: ${data.errors.length}` +
        (data.errors.length ? '\n' + data.errors.slice(0, 5).map((e) => `Row ${e.row}: ${e.reason}`).join('\n') : ''));
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-bold text-[#0D1B3D] mb-3">Add Area <span className="font-normal text-xs text-gray-400">(optional granularity)</span></h3>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <select
            value={form.city_id}
            onChange={(e) => setForm({ ...form, city_id: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Select city *</option>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Area name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="text"
            placeholder="Pincode (optional)"
            value={form.pincode}
            onChange={(e) => setForm({ ...form, pincode: e.target.value })}
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={saving}
            className="bg-[#2563EB] text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add Area'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <BulkToolbar
          selectedCount={selectedIds.size}
          onBulkDelete={handleBulkDelete}
          onImportFile={handleImportFile}
          onExport={handleExport}
          onDownloadTemplate={handleDownloadTemplate}
          showTrash={showTrash}
          onToggleTrash={toggleTrashView}
          importing={importing}
          exporting={exporting}
        />

        {showTrash ? (
          <TrashPanel
            items={trashedItems}
            loading={trashLoading}
            extraLabel="City"
            extraValue={(item) => item.city?.name}
            onRestore={handleRestore}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#EFF6FF] text-[#0D1B3D]">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={areas.length > 0 && selectedIds.size === areas.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">City</th>
                <th className="text-left px-4 py-3 font-semibold">Pincode</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
              ) : areas.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No areas yet.</td></tr>
              ) : (
                areas.map((a) => (
                  <tr key={a.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds.has(a.id)} onChange={() => toggleSelect(a.id)} />
                    </td>
                    {editingId === a.id ? (
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="border rounded px-2 py-1 text-xs w-full"
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-500">{a.city?.name || '—'}</td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editForm.pincode}
                            onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                            className="border rounded px-2 py-1 text-xs w-full"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 flex gap-3">
                          <button onClick={() => saveEdit(a.id)} className="text-xs font-semibold text-[#10B981] hover:underline">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs font-semibold text-gray-500 hover:underline">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium">{a.name}</td>
                        <td className="px-4 py-3 text-gray-500">{a.city?.name || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">{a.pincode || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${a.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {a.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 flex gap-3">
                          <button onClick={() => startEdit(a)} className="text-xs font-semibold text-[#2563EB] hover:underline">Edit</button>
                          <ConfirmDeleteButton onConfirm={() => handleDelete(a.id)} />
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function buildCategoryTree(categories, collapsedIds) {
  const byParent = new Map();
  categories.forEach((c) => {
    const key = c.parent_id || 'root';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(c);
  });

  const flat = [];
  const walk = (parentKey, depth) => {
    const children = (byParent.get(parentKey) || []).sort((a, b) => a.name.localeCompare(b.name));
    children.forEach((c) => {
      const hasChildren = (byParent.get(c.id) || []).length > 0;
      flat.push({ ...c, depth, hasChildren });
      if (!collapsedIds.has(c.id)) {
        walk(c.id, depth + 1);
      }
    });
  };
  walk('root', 0);
  return flat;
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ parent_id: '', name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', status: 'active' });
  const [collapsedIds, setCollapsedIds] = useState(new Set());

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trashedItems, setTrashedItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const toggleCollapse = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/business-categories');
      setCategories(data);
    } catch (err) {
      setError('Failed to load categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      const { data } = await api.post('/admin/business-categories', payload);
      setCategories((prev) => [...prev, data]);
      setForm({ parent_id: form.parent_id, name: '', description: '' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to add category.'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ name: c.name, description: c.description || '', status: c.status });
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.put(`/admin/business-categories/${id}`, editForm);
      setCategories((prev) => prev.map((c) => (c.id === id ? data : c)));
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update category.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/business-categories/${id}`);
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete category.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visibleIds) => {
    setSelectedIds((prev) => (prev.size === visibleIds.length ? new Set() : new Set(visibleIds)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected categor${selectedIds.size === 1 ? 'y' : 'ies'}?`)) return;
    try {
      const { data } = await api.post('/admin/business-categories/bulk-delete', { ids: Array.from(selectedIds) });
      setCategories((prev) => prev.filter((c) => !data.deleted.includes(c.id)));
      setSelectedIds(new Set());
      if (data.skipped.length > 0) {
        alert(`Deleted ${data.deleted.length}. Skipped ${data.skipped.length}:\n` + data.skipped.map((s) => `${s.name}: ${s.reason}`).join('\n'));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const loadTrashed = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get('/admin/business-categories/trashed');
      setTrashedItems(data);
    } catch (err) {
      alert('Failed to load trash.');
    } finally {
      setTrashLoading(false);
    }
  };

  const toggleTrashView = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) loadTrashed();
  };

  const handleRestore = async (ids) => {
    try {
      await api.post('/admin/business-categories/restore', { ids });
      setTrashedItems((prev) => prev.filter((i) => !ids.includes(i.id)));
      load();
    } catch (err) {
      alert('Restore failed.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/business-categories/export', { responseType: 'blob' });
      triggerDownload(res.data, 'business_categories.csv');
    } catch (err) {
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = buildCsv(
      ['parent_name', 'name', 'slug', 'description', 'status'],
      [
        ['', 'Medical', 'medical', 'Hospitals and clinics', 'active'],
        ['Medical', 'Fertility Clinic', 'fertility-clinic', 'Fertility and IVF centers', 'active'],
      ]
    );
    triggerDownload(csv, 'business_categories_template.csv');
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/business-categories/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Import done. Created: ${data.created}, Updated: ${data.updated}, Errors: ${data.errors.length}` +
        (data.errors.length ? '\n' + data.errors.slice(0, 5).map((e) => `Row ${e.row}: ${e.reason}`).join('\n') : ''));
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const tree = buildCategoryTree(categories, collapsedIds);
  const fullTree = buildCategoryTree(categories, new Set());

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-bold text-[#0D1B3D] mb-3">Add Category</h3>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={form.parent_id}
            onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Top-level category</option>
            {fullTree.map((c) => (
              <option key={c.id} value={c.id}>{'— '.repeat(c.depth)}{c.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Category name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={saving}
            className="bg-[#2563EB] text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add Category'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <BulkToolbar
          selectedCount={selectedIds.size}
          onBulkDelete={handleBulkDelete}
          onImportFile={handleImportFile}
          onExport={handleExport}
          onDownloadTemplate={handleDownloadTemplate}
          showTrash={showTrash}
          onToggleTrash={toggleTrashView}
          importing={importing}
          exporting={exporting}
        />

        {showTrash ? (
          <TrashPanel
            items={trashedItems}
            loading={trashLoading}
            extraLabel={null}
            extraValue={() => null}
            onRestore={handleRestore}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#EFF6FF] text-[#0D1B3D]">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={tree.length > 0 && selectedIds.size === tree.length}
                    onChange={() => toggleSelectAll(tree.map((c) => c.id))}
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
              ) : tree.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No categories yet.</td></tr>
              ) : (
                tree.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} />
                    </td>
                    {editingId === c.id ? (
                      <>
                        <td className="px-4 py-2" style={{ paddingLeft: `${16 + c.depth * 20}px` }}>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="border rounded px-2 py-1 text-xs w-full"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 flex gap-3">
                          <button onClick={() => saveEdit(c.id)} className="text-xs font-semibold text-[#10B981] hover:underline">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs font-semibold text-gray-500 hover:underline">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium" style={{ paddingLeft: `${16 + c.depth * 20}px` }}>
                          {c.hasChildren ? (
                            <button
                              type="button"
                              onClick={() => toggleCollapse(c.id)}
                              className="inline-flex items-center justify-center w-4 mr-1 text-gray-400 hover:text-[#2563EB]"
                            >
                              {collapsedIds.has(c.id) ? '▸' : '▾'}
                            </button>
                          ) : (
                            c.depth > 0 && <span className="text-gray-300 mr-1 inline-block w-4 text-center">└</span>
                          )}
                          {c.name}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 flex gap-3">
                          <button onClick={() => startEdit(c)} className="text-xs font-semibold text-[#2563EB] hover:underline">Edit</button>
                          <ConfirmDeleteButton onConfirm={() => handleDelete(c.id)} />
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function LocationCategoryPanel() {
  const [tab, setTab] = useState('districts');

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-100">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === t.key
                ? 'border-[#2563EB] text-[#2563EB]'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'states' && <StatesTab />}
      {tab === 'districts' && <DistrictsTab />}
      {tab === 'cities' && <CitiesTab />}
      {tab === 'areas' && <AreasTab />}
      {tab === 'categories' && <CategoriesTab />}
    </div>
  );
}